import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ═══════════════════════════════════════════════════════════════
// API CONFIG
// ═══════════════════════════════════════════════════════════════
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const apiFetch = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
      ...options,
    });
    if (!response.ok) return { error: true, status: response.status };
    return await response.json();
  } catch (err) {
    return { error: true, message: err.message };
  }
};

const ProfessionalContext = createContext();

export const useProfessional = () => {
  const context = useContext(ProfessionalContext);
  if (!context) throw new Error('useProfessional must be used within a ProfessionalProvider');
  return context;
};

export const ProfessionalProvider = ({ children }) => {
  // ── Core user data ──────────────────────────────────────────
  const [userData, setUserData] = useState({
    full_name: '',
    email: '',
    phone: '',
    profession: '',
    job_title: '',          // ← primary field used by FindJobsScreen
    current_title: '',      // ← alias from DB
    location: '',
    experience_years: '',
    skills: [],
    about: '',
    profile_image: null,
    education_level: '',
    work_preference: 'hybrid',
    salary_expectation: '',
    availability: 'immediately',
    linkedin_url: '',
    portfolio_url: '',
    github_url: '',
    customer_id: null,
    id: null,
  });

  // ── Subscription ────────────────────────────────────────────
  const [subscriptionData, setSubscriptionData] = useState({
    status: 'free',
    plan: null,
    expiry_date: null,
    features: ['Basic job search', 'Profile creation', 'Limited applications'],
    payment_history: [],
  });

  // ── Profile stats ───────────────────────────────────────────
  const [profileStats, setProfileStats] = useState({
    completion_percentage: 0,
    applications_count: 0,
    interviews_count: 0,
    offers_count: 0,
    profile_views: 0,
    saved_jobs_count: 0,
    last_active: new Date(),
  });

  // ── Preferences ─────────────────────────────────────────────
  const [preferences, setPreferences] = useState({
    job_alerts: true,
    email_notifications: true,
    push_notifications: true,
    profile_visibility: 'public',
    language: 'en',
    currency: 'GBP',
    timezone: 'Europe/London',
    theme: 'light',
  });

  // ── Career data ─────────────────────────────────────────────
  const [careerData, setCareerData] = useState({
    current_position: '',
    career_goals: '',
    interested_roles: [],
    preferred_companies: [],
    industry_focus: [],
    remote_preference: true,
    relocation_willing: false,
    notice_period: '1 month',
  });

  // ── Saved jobs ──────────────────────────────────────────────
  const [savedJobs, setSavedJobs] = useState([]);

  // ── Job applications ────────────────────────────────────────
  const [jobApplications, setJobApplications] = useState([]);

  // ── Loading state ───────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);

  // ═══════════════════════════════════════════════════════════
  // INIT
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadUserData(),
        loadSubscriptionData(),
        loadProfileStats(),
        loadPreferences(),
        loadCareerData(),
        loadSavedJobs(),
        loadApplications(),
      ]);
    } catch (error) {
      console.error('Error loading professional data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // USER DATA — loads from cache then syncs from API
  // ═══════════════════════════════════════════════════════════
  const loadUserData = async () => {
    try {
      // 1. Load from AsyncStorage cache first (instant)
      const cached = await AsyncStorage.getItem('professional_user_data');
      if (cached) {
        const parsed = JSON.parse(cached);
        const merged = normaliseUserData(parsed);
        setUserData(prev => ({ ...prev, ...merged }));
      }

      // 2. Also check userProfile cache (written by login flow)
      const profileRaw = await AsyncStorage.getItem('userProfile');
      if (profileRaw) {
        const profile = JSON.parse(profileRaw);
        const merged = normaliseUserData(profile);
        setUserData(prev => ({ ...prev, ...merged }));
      }

      // 3. Sync live from API if we have an email
      const emailRaw = await AsyncStorage.getItem('userEmail') ||
                       (cached ? JSON.parse(cached).email : null);
      if (emailRaw) {
        await syncUserFromAPI(emailRaw);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  // Normalise DB field names → context field names
  const normaliseUserData = (data) => {
    if (!data) return {};
    return {
      ...data,
      // Unify job_title from any source field
      job_title: data.job_title || data.current_title || data.profession || data.title || '',
      // Keep aliases in sync
      profession: data.profession || data.job_title || data.current_title || '',
      current_title: data.current_title || data.job_title || data.profession || '',
      full_name: data.full_name || data.name || `${data.first_name || ''} ${data.last_name || ''}`.trim() || '',
      skills: Array.isArray(data.skills) ? data.skills :
              (typeof data.skills === 'string' ? data.skills.split(',').map(s => s.trim()).filter(Boolean) : []),
      profile_image: data.profile_image || data.avatar || data.photo || null,
      id: data.id || data.customer_id || null,
      customer_id: data.customer_id || data.id || null,
    };
  };

  const syncUserFromAPI = async (email) => {
    try {
      const data = await apiFetch(`/api/data/customers?email=${encodeURIComponent(email)}&limit=1`);
      if (data && !data.error) {
        const record = Array.isArray(data) ? data[0] : (data.data?.[0] || data);
        if (record && record.email) {
          const normalised = normaliseUserData(record);
          setUserData(prev => ({ ...prev, ...normalised }));
          // Update all caches
          await AsyncStorage.setItem('professional_user_data', JSON.stringify({ ...normalised }));
          await AsyncStorage.setItem('userProfile', JSON.stringify({ ...normalised }));
          updateProfileCompletion(normalised);
        }
      }
    } catch (err) {
      console.error('syncUserFromAPI error:', err);
    }
  };

  const saveUserData = async (newData) => {
    try {
      const normalised = normaliseUserData(newData);
      const updated = { ...userData, ...normalised };
      setUserData(updated);
      await AsyncStorage.setItem('professional_user_data', JSON.stringify(updated));
      await AsyncStorage.setItem('userProfile', JSON.stringify(updated));
      updateProfileCompletion(updated);

      // Sync to API if we have an ID
      if (updated.id || updated.customer_id) {
        const id = updated.id || updated.customer_id;
        await apiFetch(`/api/data/customers/${id}`, {
          method: 'PUT',
          body: JSON.stringify({
            full_name: updated.full_name,
            phone: updated.phone,
            job_title: updated.job_title,
            profession: updated.profession,
            location: updated.location,
            experience_years: updated.experience_years,
            skills: updated.skills,
            about: updated.about,
            linkedin_url: updated.linkedin_url,
            portfolio_url: updated.portfolio_url,
          }),
        });
      }
    } catch (error) {
      console.error('Error saving user data:', error);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // SAVED JOBS
  // ═══════════════════════════════════════════════════════════
  const loadSavedJobs = async () => {
    try {
      // Load from AsyncStorage cache
      const cached = await AsyncStorage.getItem('professional_saved_jobs');
      if (cached) setSavedJobs(JSON.parse(cached));

      // Sync from API
      const data = await apiFetch('/api/data/saved_jobs?limit=100');
      if (data && !data.error && Array.isArray(data)) {
        setSavedJobs(data);
        await AsyncStorage.setItem('professional_saved_jobs', JSON.stringify(data));
        updateProfileStats({ saved_jobs_count: data.length });
      }
    } catch (err) {
      console.error('loadSavedJobs error:', err);
    }
  };

  const saveJob = async (job) => {
    try {
      const already = savedJobs.some(s => String(s.id) === String(job.id) || String(s.job_id) === String(job.id));
      if (already) return;

      const payload = {
        job_id: job.id,
        job_title: job.title,
        company: job.company,
        location: job.location,
        salary: job.salary,
        job_type: job.job_type || job.type,
        saved_at: new Date().toISOString(),
      };

      // Optimistic update
      const optimistic = [...savedJobs, { ...payload, id: `temp_${Date.now()}` }];
      setSavedJobs(optimistic);
      await AsyncStorage.setItem('professional_saved_jobs', JSON.stringify(optimistic));

      // Persist to API
      const result = await apiFetch('/api/data/saved_jobs', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      // Reload to get server IDs
      if (result && !result.error) {
        loadSavedJobs();
      }
    } catch (err) {
      console.error('saveJob error:', err);
    }
  };

  const unsaveJob = async (jobId) => {
    try {
      const entry = savedJobs.find(s => String(s.job_id) === String(jobId) || String(s.id) === String(jobId));
      const updated = savedJobs.filter(s => String(s.job_id) !== String(jobId) && String(s.id) !== String(jobId));
      setSavedJobs(updated);
      await AsyncStorage.setItem('professional_saved_jobs', JSON.stringify(updated));

      if (entry?.id && !String(entry.id).startsWith('temp_')) {
        await apiFetch(`/api/data/saved_jobs/${entry.id}`, { method: 'DELETE' });
      }
    } catch (err) {
      console.error('unsaveJob error:', err);
    }
  };

  const isJobSaved = useCallback((jobId) => {
    return savedJobs.some(s => String(s.job_id) === String(jobId) || String(s.id) === String(jobId));
  }, [savedJobs]);

  // ═══════════════════════════════════════════════════════════
  // JOB APPLICATIONS
  // ═══════════════════════════════════════════════════════════
  const loadApplications = async () => {
    try {
      const cached = await AsyncStorage.getItem('professional_applications');
      if (cached) setJobApplications(JSON.parse(cached));

      const email = userData?.email;
      if (!email) return;

      const data = await apiFetch(
        `/api/data/applications?candidate_email=${encodeURIComponent(email)}&sort=created_at&order=desc&limit=100`
      );
      if (data && !data.error && Array.isArray(data)) {
        setJobApplications(data);
        await AsyncStorage.setItem('professional_applications', JSON.stringify(data));
        updateProfileStats({ applications_count: data.length });
      }
    } catch (err) {
      console.error('loadApplications error:', err);
    }
  };

  const applyToJob = async (job, extraData = {}) => {
    try {
      const profile = userData;
      const payload = {
        job_id: job.id,
        candidate_name: profile.full_name || 'Applicant',
        candidate_email: profile.email || '',
        candidate_phone: profile.phone || '',
        candidate_location: profile.location || 'UK',
        current_title: profile.job_title || profile.profession || '',
        experience_years: profile.experience_years || 0,
        skills: Array.isArray(profile.skills) ? profile.skills : [],
        source: 'ProfessionalApp',
        applied_date: new Date().toISOString(),
        status: 'applied',
        ...extraData,
      };

      const result = await apiFetch('/api/recruitment/apply', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (result && !result.error) {
        const newApp = { ...payload, id: result.id || result.application_id || Date.now() };
        const updated = [newApp, ...jobApplications];
        setJobApplications(updated);
        await AsyncStorage.setItem('professional_applications', JSON.stringify(updated));
        incrementApplications();
        return { success: true, application: newApp };
      }
      return { success: false, error: result?.message || 'Failed to submit' };
    } catch (err) {
      console.error('applyToJob error:', err);
      return { success: false, error: err.message };
    }
  };

  const isJobApplied = useCallback((jobId) => {
    return jobApplications.some(a =>
      String(a.job_id) === String(jobId) || String(a.jobId) === String(jobId)
    );
  }, [jobApplications]);

  // ═══════════════════════════════════════════════════════════
  // SUBSCRIPTION
  // ═══════════════════════════════════════════════════════════
  const loadSubscriptionData = async () => {
    try {
      const [profSub, bizSub, history] = await Promise.all([
        AsyncStorage.getItem('jobeng_professional_subscription'),
        AsyncStorage.getItem('jobeng_business_subscription'),
        AsyncStorage.getItem('payment_history'),
      ]);

      let status = 'free';
      let plan = null;
      let features = ['Basic job search', 'Profile creation', 'Limited applications'];

      if (profSub === 'active') {
        status = 'professional';
        plan = 'Professional';
        features = [
          'Unlimited job applications', 'Premium job access', 'AI career matching',
          'Skills development courses', 'Career coaching sessions',
          'Priority application processing', 'Advanced profile analytics', 'Direct employer messaging',
        ];
      } else if (bizSub === 'active') {
        status = 'business';
        plan = 'Business';
        features = [
          'All Professional features', 'Talent recruitment tools', 'Project management suite',
          'Compliance tracking', 'Team collaboration tools', 'Advanced analytics dashboard',
          'Custom branding options', 'Dedicated account manager',
        ];
      }

      setSubscriptionData({
        status, plan,
        expiry_date: status !== 'free' ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) : null,
        features,
        payment_history: history ? JSON.parse(history) : [],
      });
    } catch (err) {
      console.error('loadSubscriptionData error:', err);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // PROFILE STATS
  // ═══════════════════════════════════════════════════════════
  const loadProfileStats = async () => {
    try {
      const stored = await AsyncStorage.getItem('professional_profile_stats');
      if (stored) {
        setProfileStats(JSON.parse(stored));
      }
    } catch (err) {
      console.error('loadProfileStats error:', err);
    }
  };

  const updateProfileStats = async (newStats) => {
    try {
      setProfileStats(prev => {
        const updated = { ...prev, ...newStats };
        AsyncStorage.setItem('professional_profile_stats', JSON.stringify(updated)).catch(() => {});
        return updated;
      });
    } catch (err) {
      console.error('updateProfileStats error:', err);
    }
  };

  const updateProfileCompletion = (data = userData) => {
    const required = ['full_name', 'email', 'phone', 'job_title', 'location', 'experience_years', 'about', 'education_level'];
    const optional = ['skills', 'work_preference', 'salary_expectation', 'linkedin_url'];
    const reqDone = required.filter(f => data[f] && String(data[f]).trim()).length;
    const optDone = optional.filter(f => data[f] && (Array.isArray(data[f]) ? data[f].length > 0 : String(data[f]).trim())).length;
    const pct = Math.round((reqDone / required.length) * 70 + (optDone / optional.length) * 30);
    updateProfileStats({ completion_percentage: pct });
  };

  // ═══════════════════════════════════════════════════════════
  // PREFERENCES & CAREER DATA
  // ═══════════════════════════════════════════════════════════
  const loadPreferences = async () => {
    try {
      const stored = await AsyncStorage.getItem('professional_preferences');
      if (stored) setPreferences(prev => ({ ...prev, ...JSON.parse(stored) }));
    } catch (err) {}
  };

  const savePreferences = async (newPrefs) => {
    try {
      const updated = { ...preferences, ...newPrefs };
      setPreferences(updated);
      await AsyncStorage.setItem('professional_preferences', JSON.stringify(updated));
    } catch (err) {}
  };

  const loadCareerData = async () => {
    try {
      const stored = await AsyncStorage.getItem('professional_career_data');
      if (stored) setCareerData(prev => ({ ...prev, ...JSON.parse(stored) }));
    } catch (err) {}
  };

  const saveCareerData = async (newData) => {
    try {
      const updated = { ...careerData, ...newData };
      setCareerData(updated);
      await AsyncStorage.setItem('professional_career_data', JSON.stringify(updated));
    } catch (err) {}
  };

  // ═══════════════════════════════════════════════════════════
  // COUNTERS & HELPERS
  // ═══════════════════════════════════════════════════════════
  const incrementApplications = () => updateProfileStats({ applications_count: profileStats.applications_count + 1 });
  const incrementInterviews = () => updateProfileStats({ interviews_count: profileStats.interviews_count + 1 });
  const incrementOffers = () => updateProfileStats({ offers_count: profileStats.offers_count + 1 });
  const updateLastActive = () => updateProfileStats({ last_active: new Date() });

  const isPremiumUser = () => subscriptionData.status !== 'free';
  const hasFeature = (feature) => subscriptionData.features.some(f => f.toLowerCase().includes(feature.toLowerCase()));
  const getSubscriptionFeatures = () => subscriptionData.features;

  const getGreeting = () => {
    const hour = new Date().getHours();
    const name = userData.full_name?.split(' ')[0] || 'Professional';
    if (hour < 12) return `Good morning, ${name}!`;
    if (hour < 17) return `Good afternoon, ${name}!`;
    return `Good evening, ${name}!`;
  };

  const getProfileCompletionTips = () => {
    const tips = [];
    if (!userData.full_name) tips.push('Add your full name');
    if (!userData.job_title && !userData.profession) tips.push('Specify your job title');
    if (!userData.about) tips.push('Write an about section');
    if (!userData.skills?.length) tips.push('Add your skills');
    if (!userData.linkedin_url) tips.push('Add your LinkedIn profile');
    if (!userData.portfolio_url) tips.push('Add your portfolio URL');
    return tips;
  };

  // ═══════════════════════════════════════════════════════════
  // CONTEXT VALUE
  // ═══════════════════════════════════════════════════════════
  const contextValue = {
    // Data
    userData,
    subscriptionData,
    profileStats,
    preferences,
    careerData,
    savedJobs,
    jobApplications,
    isLoading,

    // User actions
    saveUserData,
    syncUserFromAPI,

    // Job actions
    saveJob,
    unsaveJob,
    isJobSaved,
    applyToJob,
    isJobApplied,

    // Stat actions
    savePreferences,
    saveCareerData,
    updateProfileStats,
    incrementApplications,
    incrementInterviews,
    incrementOffers,
    updateLastActive,
    updateProfileCompletion,

    // Helpers
    getSubscriptionFeatures,
    hasFeature,
    isPremiumUser,
    getGreeting,
    getProfileCompletionTips,

    // Reload
    loadAllData,
    loadApplications,
    loadSavedJobs,
  };

  return (
    <ProfessionalContext.Provider value={contextValue}>
      {children}
    </ProfessionalContext.Provider>
  );
};
